/* =========================================================================
   Image helpers — Blob <-> data URL, and object-URL lifecycle.
   -------------------------------------------------------------------------
   Inside the app photos are Blobs (what IndexedDB stores efficiently).
   For display we need object URLs; for export we need data URLs so the
   exported .json stays a single portable file that iteration 1 can also read.
   ========================================================================= */

// Blob -> object URL, cached so repeated renders don't leak a new URL each time.
const urlCache = new WeakMap()

/** Object URL for a Blob, safe to call on every render. */
export function blobUrl(blob) {
  if (!blob) return null
  // Legacy records may still hold a data-URL string; pass those straight through.
  if (typeof blob === 'string') return blob
  let u = urlCache.get(blob)
  if (!u) {
    u = URL.createObjectURL(blob)
    urlCache.set(blob, u)
  }
  return u
}

/** Release a Blob's object URL. Call when a photo is deleted or replaced. */
export function releaseBlobUrl(blob) {
  if (!blob || typeof blob === 'string') return
  const u = urlCache.get(blob)
  if (u) {
    URL.revokeObjectURL(u)
    urlCache.delete(blob)
  }
}

/** "data:image/jpeg;base64,..." -> Blob */
export function dataUrlToBlob(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return null
  if (!dataUrl.startsWith('data:')) return null
  const [head, body] = dataUrl.split(',')
  const mime = (head.match(/data:([^;]+)/) || [, 'image/jpeg'])[1]
  const isB64 = /;base64/i.test(head)
  const bytes = isB64 ? atob(body) : decodeURIComponent(body)
  const buf = new Uint8Array(bytes.length)
  for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i)
  return new Blob([buf], { type: mime })
}

/** Blob -> "data:image/jpeg;base64,..." (used only when exporting) */
export function blobToDataUrl(blob) {
  if (!blob) return Promise.resolve(null)
  if (typeof blob === 'string') return Promise.resolve(blob)
  return new Promise((resolve, reject) => {
    const rd = new FileReader()
    rd.onload = () => resolve(rd.result)
    rd.onerror = () => reject(rd.error)
    rd.readAsDataURL(blob)
  })
}

/** Canvas -> Blob, promisified. Falls back to toDataURL on very old engines. */
export function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.85) {
  return new Promise(resolve => {
    if (canvas.toBlob) {
      canvas.toBlob(b => resolve(b || dataUrlToBlob(canvas.toDataURL(type, quality))), type, quality)
    } else {
      resolve(dataUrlToBlob(canvas.toDataURL(type, quality)))
    }
  })
}

/**
 * Load a File/Blob into an HTMLImageElement.
 *
 * iPhone photos are often HEIC. Safari decodes HEIC natively, so this works on
 * an iPhone; on a desktop browser that can't decode it we reject with a clear
 * message rather than failing silently.
 */
export function loadImage(src) {
  return new Promise((resolve, reject) => {
    const url = src instanceof Blob ? URL.createObjectURL(src) : src
    const img = new Image()
    img.onload = () => {
      if (src instanceof Blob) URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      if (src instanceof Blob) URL.revokeObjectURL(url)
      reject(new Error('unreadable-image'))
    }
    img.src = url
  })
}

/**
 * Downscale an image File/Blob and return raw base64 (no data: prefix),
 * ready to send to the vision endpoint.
 *
 * 1568px is the point above which Claude downsamples anyway, so sending more
 * just costs upload time. JPEG keeps the payload small — a 1.6MB PNG
 * screenshot lands around 150KB.
 */
export async function toBase64Jpeg(fileOrBlob, maxEdge = 1568, quality = 0.85) {
  const img = await loadImage(fileOrBlob)
  const s = Math.min(1, maxEdge / Math.max(img.width, img.height))
  const c = document.createElement('canvas')
  c.width = Math.round(img.width * s)
  c.height = Math.round(img.height * s)
  const ctx = c.getContext('2d')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, c.width, c.height)
  ctx.drawImage(img, 0, 0, c.width, c.height)
  const dataUrl = c.toDataURL('image/jpeg', quality)
  return { base64: dataUrl.split(',')[1], mediaType: 'image/jpeg' }
}

/** Rough byte size of a recipe's photos, for the storage indicator. */
export function recipeImageBytes(r) {
  let n = 0
  if (r.heroImage instanceof Blob) n += r.heroImage.size
  for (const im of r.images || []) if (im instanceof Blob) n += im.size
  return n
}

export function formatBytes(n) {
  if (!n) return '0 KB'
  if (n < 1024 * 1024) return Math.round(n / 1024) + ' KB'
  return (n / 1024 / 1024).toFixed(1) + ' MB'
}
